import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';
import {
  AnyMasonryEngineNode,
  MasonryEngineNode,
  MasonryEngineNodeQueryParams,
} from '../types/masonry-engine-node.types';
import { MasonryNodeReloadMessage } from '../types/masonry-engine-message.types';
import { clearNodeCache } from '../utils/clear-node-cache';

type ReloadPromiseResolvers = {
  resolve: (node: AnyMasonryEngineNode) => void;
  reject: (error: unknown) => void;
};

type UseReloadReturn = {
  reloadStatus:
    | MasonryNodeReloadMessage['payload']['renderIntermediately']
    | false;
  triggerReload: (
    payload: MasonryNodeReloadMessage['payload'],
  ) => Promise<AnyMasonryEngineNode>;
};

type UseReloadOptions = {
  id: MasonryEngineNode['id'];
  /**
   * The return of `useQuery` used to fetch the node data, as-is.
   */
  query: UseQueryResult<AnyMasonryEngineNode>;

  /**
   * The stateful value for query params that can be set by `reload` actions. We
   * let `NodeResolver` own this, rather than own it internally in this hook,
   * because:
   * 1. It needs to be passed to the `useQuery` call as a dependency.
   * 2. But this hook, by definition, is called AFTER the call to `useQuery`,
   *    since it needs the query object as input.
   */
  queryParamsRef: MutableRefObject<MasonryEngineNodeQueryParams | undefined>;

  /**
   * Since `NodeResolver` owns the stateful value for query params, it
   * also needs to provide the setter function so this hook can update it when
   * needed.
   */
  setQueryParams: (newQueryParams: MasonryEngineNodeQueryParams) => void;
};

/**
 * Works together with `<NodeResolver>`'s query object, as well as its local
 * state for `queryParams`, to provide a `triggerReload` function that
 * can be used to reload the node data, optionally updating the query params.
 *
 * Encapsulates the logic for determining which refetching strategy to use based
 * on whether the query params have changed, and returns a promise which can be
 * passed back to the invoker of the `reload` message. The promise resolves or
 * rejects based on the outcome of the query refetch.
 */
export const useReload = ({
  id,
  query,
  queryParamsRef,
  setQueryParams,
}: UseReloadOptions): UseReloadReturn => {
  /**
   * This will store JUST the "resolve" and "reject" callbacks of the promise
   * we will create when `triggerReload` is called.
   */
  const reloadPromiseResolvers = useRef<ReloadPromiseResolvers>();

  const reloadStrategyRef = useRef<'queryParams' | 'refetch'>();

  const [reloadStatus, setReloadStatus] = useState<
    MasonryNodeReloadMessage['payload']['renderIntermediately'] | false
  >(false);

  const { refetch } = query;

  /**
   * This function will be returned to `NodeResolver` so that it can be called
   * when `reload` messages arrive, and its promise returned to the sender of
   * the message.
   */
  const triggerReload = useCallback(
    (payload: MasonryNodeReloadMessage['payload']) => {
      if (reloadPromiseResolvers.current) {
        /**
         * This function was called WHILE a previous reload request was still
         * in-flight. We need to reject the previous promise since it's now
         * being superseded by this new request.
         */
        reloadPromiseResolvers.current.reject(
          new Error('Superseded by a newer reload request'),
        );

        reloadPromiseResolvers.current = undefined;
      }

      const reloadPromise = new Promise<AnyMasonryEngineNode>(
        (resolve, reject) => {
          reloadPromiseResolvers.current = {
            resolve,
            reject,
          };
        },
      );

      const newQueryParams = payload.setQueryParams?.(
        queryParamsRef.current || {},
      );

      if (newQueryParams && !isEqual(queryParamsRef.current, newQueryParams)) {
        /**
         * The query params are changing, so the appropriate strategy for
         * causing a refetch is to update the stateful value for them, which
         * `NodeResolver` passes to `useQuery`. This will cause the query to
         * refetch automatically.
         */
        reloadStrategyRef.current = 'queryParams';
        setReloadStatus(payload.renderIntermediately);
        setQueryParams(newQueryParams);
      } else {
        /**
         * The query params are staying the same, so we can simply call
         * `refetch` on the query object to trigger a refetch.
         */
        reloadStrategyRef.current = 'refetch';
        setReloadStatus(payload.renderIntermediately);
        refetch();
      }

      /**
       * We will resolve or reject this promise later, in the `useEffect` below,
       * when we detect that the query has either succeeded or failed.
       */
      return reloadPromise;
    },
    [queryParamsRef, refetch, setQueryParams],
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    if (reloadStrategyRef.current === 'refetch') {
      /**
       * This is a "pure" reload (not triggered by query param changes). Therefore, we need to
       * clear the cache for this node's descendants as soon as they unmount,
       * before they re-mount once more.
       *
       * in "loader" mode, we use "removeQueries" because we don't want remounting
       * descendants to show cached data even for a second; in "self" mode, we use "invalidateQueries"
       * because by definition the entire tree is supposed to stay on the screen and
       * update "in-place".
       */
      clearNodeCache({
        id,
        queryClient,
        descendants: true,
        strategy: reloadStatus === 'loader' ? 'remove' : 'invalidate',
      });
    }
  }, [id, queryClient, reloadStatus]);

  useEffect(() => {
    const resolvers = reloadPromiseResolvers.current;

    if (!resolvers) {
      return;
    }

    const strategy = reloadStrategyRef.current;

    /**
     * First, we want to manage when to set `reloadStatus` back to `false`,
     * so that the loader is no longer rendered.
     */

    if (
      /**
       * If this is a "pure" reload - i.e., not triggered by query param
       * changes, but by an imperative call to `refetch()` - then we ONLY want
       * to stop rendering the loader once the refetch is complete.
       *
       * The implication is that "pure" reloads want to FORGET cached data.
       */
      strategy === 'refetch' &&
      !query.isFetching
    ) {
      setReloadStatus(false);
    } else if (
      /**
       * On the other hand, If this reload was triggered by query param changes,
       * Then we want to stop rendering the loader as soon as we have data for
       * the current query params - whether it's cached data acquired by a
       * previous request with the same params, or fresh data if we have no
       * cache for this set of params.
       *
       * The implication is that "change query params" reloads want to UTILIZE
       * cached data.
       */
      strategy === 'queryParams' &&
      query.data &&
      !query.isPreviousData
    ) {
      setReloadStatus(false);
    }

    /**
     * Next, we want to manage when the reload promise should be resolved or rejected.
     */

    if (!query.isFetching) {
      /**
       * DEFINITELY stop rendering the loader now, if we haven't already.
       */
      setReloadStatus(false);
      if (query.isSuccess) {
        resolvers.resolve(query.data);
      } else {
        resolvers.reject(query.error);
      }

      reloadPromiseResolvers.current = undefined;
      reloadStrategyRef.current = undefined;
    }
  }, [
    query.data,
    query.isPreviousData,
    query.isFetching,
    query.isSuccess,
    query.error,
  ]);

  useEffect(
    () => () => {
      if (reloadPromiseResolvers.current) {
        /**
         * The component is unmounting while a reload request is still
         * in-flight. This is fine from the perspective of the component, but we
         * have no choice but to reject the promise we returned to the invoker
         * of the reload, since there's no way to resolve it anymore.
         */
        reloadPromiseResolvers.current.reject(
          new Error('Component unmounted before reload could complete'),
        );
      }
    },
    [],
  );

  return {
    triggerReload,
    reloadStatus,
  };
};
